import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import {
  EmptyState, ListTable, MetricCard, PageTabs, Panel, SearchField, StatusChip, ToolbarButton,
  type ListColumn, type StatusTone,
} from '../../../ui-kit';
import {
  fetchAccount, planDocument, watchRequests,
  type PsAccount, type PsEnrollment, type PsInvoice, type PsInvoiceLine, type PsMember, type PsPlan,
  type PsRequestCard,
} from '../../../services/policyServicingService';
import { fromNow, matches, useLive, when } from '../rocco/live';
import RequestStoryModal from './RequestStory';
import { dayOf, dollars } from './format';
import { statusChip } from './status';
import { useAgent } from '../AgentContext';

type TabId = 'overview' | 'plans' | 'members' | 'enrollments' | 'billing' | 'requests';

const TABS: { value: TabId; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'plans', label: 'Plans' },
  { value: 'members', label: 'Members' },
  { value: 'enrollments', label: 'Enrollments' },
  { value: 'billing', label: 'Billing' },
  { value: 'requests', label: 'Service requests' },
];

/** What a carrier has said about something we sent it. "Unknown" is the one that needs a call. */
function carrierChip(status: string): { tone: StatusTone; label: string } {
  const lowered = status.toLowerCase();
  if (lowered === 'unknown') return { tone: 'warning', label: 'Not heard back' };
  if (lowered === 'active' || lowered === 'confirmed') return { tone: 'good', label: status };
  if (lowered === 'pending') return { tone: 'warning', label: status };
  return { tone: 'neutral', label: status };
}

function memberChip(status: string): { tone: StatusTone; label: string } {
  if (status === 'Active') return { tone: 'good', label: status };
  if (status === 'Terminated') return { tone: 'neutral', label: status };
  return { tone: 'warning', label: status };
}

const text = (t: import('@mui/material').Theme) => ({ color: t.tokens.text.primary });
const quiet = (t: import('@mui/material').Theme) => ({ color: t.tokens.text.tertiary });
const figures = (t: import('@mui/material').Theme) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' });

/**
 * One employer group, as the agency holds it: its plans and their rules, its
 * employees, what has been sent to the carriers and what they have said, what
 * they billed, and every request its HR contacts have sent in.
 *
 * This is what the agent checks before it answers. Where a carrier's status
 * reads "Not heard back", only the carrier can answer, and a call fills it in
 * here while you watch.
 */
export default function AccountPage({ accountId }: { accountId: string }) {
  const { agent, base } = useAgent();
  const fetcher = React.useCallback(() => fetchAccount(agent.id, accountId), [agent.id, accountId]);
  const { data: account, error } = useLive(fetcher, 5000, { watch: watchRequests });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = React.useState<string | null>(null);
  const tab = (TABS.find((t) => t.value === params.get('tab'))?.value ?? 'overview') as TabId;
  const go = (next: TabId) => setParams(next === 'overview' ? {} : { tab: next }, { replace: true });
  const back = () => navigate(`${base}/accounts`);

  if (error && !account) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <ToolbarButton variant="ghost" icon={<ArrowBackRounded />} onClick={back}>Accounts</ToolbarButton>
        <EmptyState message={error} />
      </Stack>
    );
  }
  const loading = !account;
  const facts = account ? [
    `Group ${account.group_number}`, account.industry, account.location,
    `${account.counts.employees} employees`, account.renewal_on ? `Renews ${dayOf(account.renewal_on)}` : null,
  ].filter(Boolean) : [];

  return (
    <Stack spacing={2} sx={{ pb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <ToolbarButton variant="outlined" icon={<ArrowBackRounded />} aria-label="Back to Accounts"
          tooltip="Back to Accounts" onClick={back} />
        <Stack sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="h4" sx={text}>
            {account ? account.name : <Skeleton variant="text" width={240} />}
          </Typography>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
            {account ? facts.join('  ·  ') : <Skeleton variant="text" width={380} />}
          </Typography>
        </Stack>
      </Stack>

      <Box>
        <PageTabs tabs={TABS} value={tab} onChange={go} ariaLabel="Account" />
        {tab === 'overview' ? <Overview account={account} loading={loading} onTab={go} onOpen={setOpen} /> : null}
        {tab === 'plans' ? <Plans account={account} loading={loading} /> : null}
        {tab === 'members' ? <Members members={account?.members ?? []} loading={loading} /> : null}
        {tab === 'enrollments'
          ? <Enrollments rows={account?.enrollments ?? []} loading={loading} onOpen={setOpen} /> : null}
        {tab === 'billing' ? <Billing invoices={account?.invoices ?? []} loading={loading} onOpen={setOpen} /> : null}
        {tab === 'requests' ? <Requests rows={account?.requests ?? []} loading={loading} onOpen={setOpen} /> : null}
      </Box>
      {open ? <RequestStoryModal requestId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}

/** A label and its value on one line, the value on the right. Both wrap. */
function Fact({ label, value, detail }: { label: string; value: React.ReactNode; detail?: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, flexShrink: 0, maxWidth: '45%' })}>
        {label}
      </Typography>
      <Stack sx={{ minWidth: 0, alignItems: 'flex-end' }}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, textAlign: 'right' })}>{value}</Typography>
        {detail ? <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, textAlign: 'right' })}>{detail}</Typography> : null}
      </Stack>
    </Stack>
  );
}

// --- Overview ---------------------------------------------------------------------

function Overview({
  account, loading, onTab, onOpen,
}: { account: PsAccount | null; loading: boolean; onTab: (tab: TabId) => void; onOpen: (id: string) => void }) {
  const waiting = (account?.enrollments ?? []).filter((e) => e.carrier_status.toLowerCase() === 'unknown');
  const counts = account?.counts;
  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        <MetricCard loading={loading} label="Employees" value={(counts?.employees ?? 0).toLocaleString()}
          info="Everyone on this employer's roster, whatever their coverage." />
        <MetricCard loading={loading} label="Active" value={(counts?.active ?? 0).toLocaleString()}
          info="Employees whose coverage is in force on our records." />
        <MetricCard loading={loading} label="Not heard back from the carrier" value={(counts?.waiting_on_carrier ?? 0).toLocaleString()}
          info="Enrollments and terminations we sent that the carrier has not confirmed to us. Only the carrier can say where these stand." />
        <MetricCard loading={loading} label="Service requests" value={(counts?.requests ?? 0).toLocaleString()}
          note={counts ? `${counts.open_requests} being worked on` : undefined}
          info="Emails this employer's HR contacts have sent in about their employees." />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="Not heard back from the carrier" loading={loading} loadingHeight={120}
          subtitle="Sent by us, not yet confirmed by the carrier"
          actions={<ToolbarButton variant="ghost" onClick={() => onTab('enrollments')}>All enrollments</ToolbarButton>}>
          {waiting.length ? (
            <Stack spacing={1.25}>
              {waiting.map((e) => (
                <Stack key={e.id} direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={text}>{e.member} · {e.kind}</Typography>
                    <Typography variant="caption" sx={quiet}>
                      {[e.plan, `sent ${dayOf(e.submitted_on)}`, `effective ${dayOf(e.effective_on)}`].join(' · ')}
                    </Typography>
                  </Stack>
                  <StatusChip {...carrierChip(e.carrier_status)} />
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={quiet}>Nothing waiting. The carriers have confirmed everything we sent.</Typography>
          )}
        </Panel>

        <Panel title="Group rules" loading={loading} loadingHeight={120}
          subtitle="From the plan documents. The agent answers from these without calling anyone">
          <Stack spacing={1.25}>
            {(account?.rules ?? []).map((rule) => (
              <Fact key={rule.label} label={rule.label} value={rule.value} detail={rule.detail} />
            ))}
          </Stack>
        </Panel>

        <Panel title="Plans" loading={loading} loadingHeight={100}
          actions={<ToolbarButton variant="ghost" onClick={() => onTab('plans')}>Benefits and documents</ToolbarButton>}>
          <Stack spacing={1.25}>
            {(account?.plans ?? []).map((plan) => (
              <Fact key={plan.id} label={plan.line} value={plan.name} detail={`${plan.enrolled} enrolled · ${plan.carrier}`} />
            ))}
          </Stack>
        </Panel>

        <Panel title="HR contacts" loading={loading} loadingHeight={100}
          subtitle="An email from one of these is taken as a request about this employer">
          <Stack spacing={1.25}>
            {(account?.contacts ?? []).map((contact) => (
              <Fact key={contact.email ?? contact.name ?? ''} label={contact.name ?? '—'} value={contact.email ?? '—'}
                detail={contact.title ?? undefined} />
            ))}
          </Stack>
        </Panel>
      </Box>

      <Requests rows={(account?.requests ?? []).slice(0, 5)} loading={loading} onOpen={onOpen} title="Latest service requests"
        footer={<ToolbarButton variant="ghost" onClick={() => onTab('requests')}>All service requests</ToolbarButton>} />
    </Stack>
  );
}

// --- Plans ------------------------------------------------------------------------

function Plans({ account, loading }: { account: PsAccount | null; loading: boolean }) {
  if (loading || !account) {
    return (
      <Stack spacing={2}>
        <Panel title=" " loading loadingHeight={260}>{null}</Panel>
        <Panel title=" " loading loadingHeight={260}>{null}</Panel>
      </Stack>
    );
  }
  return (
    <Stack spacing={2}>
      {account.plans.map((plan) => <PlanPanel key={plan.id} accountId={account.id} plan={plan} />)}
    </Stack>
  );
}

const DOCUMENT_HEIGHT = 300;

function PlanPanel({ accountId, plan }: { accountId: string; plan: PsPlan }) {
  const agentId = useAgent().agent.id;
  const [url, setUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    let made: string | null = null;
    planDocument(agentId, accountId, plan.id)
      .then((found) => { made = found; if (live) setUrl(found); else URL.revokeObjectURL(found); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [agentId, accountId, plan.id]);

  return (
    <Panel
      title={plan.name}
      subtitle={[plan.line, plan.carrier, plan.plan_year, `${plan.enrolled} enrolled`].filter(Boolean).join(' · ')}
      actions={(
        <ToolbarButton variant="outlined" icon={<OpenInNewRounded />} disabled={!url}
          onClick={() => { if (url) window.open(url, '_blank', 'noopener'); }}>
          Open the plan document
        </ToolbarButton>
      )}
    >
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, alignItems: 'start' }}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="overline" sx={quiet}>What the plan pays</Typography>
            {plan.benefits.map((benefit) => <Fact key={benefit.label} label={benefit.label} value={benefit.value} />)}
          </Stack>
          <Stack spacing={1}>
            <Typography variant="overline" sx={quiet}>Monthly premium</Typography>
            {plan.rates.map((rate) => <Fact key={rate.tier} label={rate.tier} value={dollars(rate.amount)} />)}
          </Stack>
        </Stack>
        <Stack spacing={0.75}>
          <Typography variant="overline" sx={quiet}>Summary of benefits · the document on file</Typography>
          {/* The top of page one, as a picture: the button above opens the whole document. */}
          <Box sx={(t) => ({
            height: DOCUMENT_HEIGHT, overflow: 'hidden', borderRadius: `${t.ds.radius.md}px`,
            border: `1px solid ${t.tokens.border.subtle}`, bgcolor: t.tokens.surface.sunken,
          })}>
            {url ? (
              <Box component="iframe" title="" aria-hidden tabIndex={-1} scrolling="no"
                src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                sx={{ width: '100%', height: DOCUMENT_HEIGHT * 3, border: 0, pointerEvents: 'none', display: 'block' }} />
            ) : failed ? (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 2 }}>
                <Typography variant="body2" sx={quiet}>The document could not be opened. The fields on the left are the same benefits.</Typography>
              </Box>
            ) : <Skeleton variant="rectangular" height={DOCUMENT_HEIGHT} aria-hidden />}
          </Box>
        </Stack>
      </Box>
    </Panel>
  );
}

// --- Members ----------------------------------------------------------------------

function Members({ members, loading }: { members: PsMember[]; loading: boolean }) {
  const [query, setQuery] = React.useState('');
  const rows = members.filter((m) => matches(query, [m.name, m.job_title, m.status, m.medical, m.dental, m.employment]));
  const columns: ListColumn<PsMember>[] = [
    {
      key: 'name', header: 'Employee', sortValue: (m) => m.name, grow: 1.2, minWidth: 190,
      render: (m) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={text}>{m.name}</Typography>
          <Typography variant="caption" sx={quiet}>{[m.job_title, m.employment].filter(Boolean).join(' · ')}</Typography>
        </Stack>
      ),
    },
    {
      key: 'hired', header: 'Start date', sortValue: (m) => m.hired_on, sortStartsDescending: true, width: 130,
      render: (m) => <Typography variant="body2" sx={figures}>{dayOf(m.hired_on)}</Typography>,
    },
    {
      key: 'dob', header: 'Date of birth', sortValue: (m) => m.date_of_birth, width: 130,
      render: (m) => (m.date_of_birth === 'Missing'
        ? <StatusChip tone="warning" label="Missing" />
        : <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>On file</Typography>),
    },
    {
      key: 'medical', header: 'Medical', sortValue: (m) => m.medical, grow: 0.8, minWidth: 150,
      render: (m) => <Typography variant="body2" sx={m.medical === 'Not enrolled' ? quiet : text}>{m.medical}</Typography>,
    },
    {
      key: 'dental', header: 'Dental', sortValue: (m) => m.dental, grow: 0.8, minWidth: 150,
      render: (m) => <Typography variant="body2" sx={m.dental === 'Not enrolled' ? quiet : text}>{m.dental}</Typography>,
    },
    {
      key: 'status', header: 'Status', sortValue: (m) => m.status, width: 190,
      render: (m) => <StatusChip {...memberChip(m.status)} />,
    },
  ];
  const active = members.filter((m) => m.status === 'Active').length;
  return (
    <Panel
      title="Members"
      subtitle={`${members.length} employees · ${active} active · ${members.length - active} with something in flight`}
      search={<SearchField value={query} onChange={setQuery} placeholder="Search employees…" />}
    >
      <ListTable columns={columns} rows={rows} getRowKey={(m) => m.id} pageSize={15} loading={loading}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage={members.length ? 'Nothing matches. Clear the search.' : 'No employees on this roster yet.'} />
    </Panel>
  );
}

// --- Enrollments ------------------------------------------------------------------

function Enrollments({
  rows: all, loading, onOpen,
}: { rows: PsEnrollment[]; loading: boolean; onOpen: (id: string) => void }) {
  const [query, setQuery] = React.useState('');
  const rows = all.filter((e) => matches(query, [e.member, e.plan, e.kind, e.carrier_status, e.internal_status, e.reason,
    e.next_action, e.reference]));
  const columns: ListColumn<PsEnrollment>[] = [
    {
      key: 'member', header: 'Employee', sortValue: (e) => e.member, grow: 1, minWidth: 160,
      render: (e) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={text}>{e.member}</Typography>
          <Typography variant="caption" sx={quiet}>{e.kind} · {e.plan}</Typography>
        </Stack>
      ),
    },
    {
      key: 'submitted', header: 'Sent', sortValue: (e) => e.submitted_on, sortStartsDescending: true, width: 120,
      render: (e) => <Typography variant="body2" sx={figures}>{dayOf(e.submitted_on)}</Typography>,
    },
    {
      key: 'effective', header: 'Effective', sortValue: (e) => e.effective_on, sortStartsDescending: true, width: 120,
      render: (e) => <Typography variant="body2" sx={figures}>{dayOf(e.effective_on)}</Typography>,
    },
    {
      key: 'ours', header: 'Our status', sortValue: (e) => e.internal_status, width: 120,
      render: (e) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{e.internal_status}</Typography>,
    },
    {
      key: 'carrier', header: "Carrier's status", sortValue: (e) => e.carrier_status, width: 160,
      render: (e) => <StatusChip {...carrierChip(e.carrier_status)} />,
    },
    {
      key: 'said', header: 'What the carrier said', grow: 1.6, minWidth: 220,
      render: (e) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={e.reason ? text : quiet}>
            {e.reason || (e.carrier_status.toLowerCase() === 'unknown' ? 'Nothing yet. Only the carrier can say.' : '—')}
          </Typography>
          {e.next_action || e.reference ? (
            <Typography variant="caption" sx={quiet}>
              {[e.next_action && `Next: ${e.next_action}`, e.reference && `ref ${e.reference}`].filter(Boolean).join(' · ')}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
  ];
  const waiting = all.filter((e) => e.carrier_status.toLowerCase() === 'unknown').length;
  return (
    <Panel
      title="Enrollments"
      subtitle={`${all.length} enrollments and terminations sent to the carriers · ${waiting} not heard back on · those first`}
      search={<SearchField value={query} onChange={setQuery} placeholder="Search enrollments…" />}
    >
      <ListTable columns={columns} rows={rows} getRowKey={(e) => e.id} pageSize={15} loading={loading}
        onRowClick={(e) => { if (e.request_id) onOpen(e.request_id); }}
        emptyMessage={all.length ? 'Nothing matches. Clear the search.' : 'Nothing has been sent to a carrier yet.'} />
    </Panel>
  );
}

// --- Billing ----------------------------------------------------------------------

function Billing({
  invoices, loading, onOpen,
}: { invoices: PsInvoice[]; loading: boolean; onOpen: (id: string) => void }) {
  if (loading) {
    return (
      <Stack spacing={2}>
        <Panel title=" " loading loadingHeight={220}>{null}</Panel>
        <Panel title=" " loading loadingHeight={220}>{null}</Panel>
      </Stack>
    );
  }
  if (!invoices.length) return <EmptyState message="No invoices on file for this employer yet." />;
  return (
    <Stack spacing={2}>
      {invoices.map((invoice, index) => (
        <InvoicePanel key={invoice.id} invoice={invoice} onOpen={onOpen}
          // The newest month and anything a call has touched open; the rest are a click away.
          collapsed={index > 1 && !invoice.lines.some((l) => l.note || l.kind === 'credit')} />
      ))}
    </Stack>
  );
}

function InvoicePanel({
  invoice, collapsed, onOpen,
}: { invoice: PsInvoice; collapsed: boolean; onOpen: (id: string) => void }) {
  const columns: ListColumn<PsInvoiceLine>[] = [
    {
      key: 'member', header: 'Employee', sortValue: (l) => l.member, grow: 1, minWidth: 170,
      render: (l) => <Typography variant="body2" sx={text}>{l.member}</Typography>,
    },
    {
      key: 'tier', header: 'Coverage', sortValue: (l) => l.tier, grow: 0.8, minWidth: 150,
      render: (l) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          {l.kind === 'credit' ? 'Credit' : l.tier}
        </Typography>
      ),
    },
    {
      key: 'note', header: 'Note', grow: 1.6, minWidth: 220,
      render: (l) => (l.note
        ? <Typography variant="body2" sx={text}>{l.note}</Typography>
        : <Typography variant="body2" sx={quiet}>—</Typography>),
    },
    {
      key: 'amount', header: 'Amount', sortValue: (l) => l.amount, sortStartsDescending: true, width: 130, align: 'right',
      render: (l) => (
        <Typography variant="body2" sx={(t) => ({
          fontVariantNumeric: 'tabular-nums',
          color: l.kind === 'credit' ? t.tokens.semantic.success : t.tokens.text.primary,
        })}>
          {dollars(l.amount)}
        </Typography>
      ),
    },
  ];
  const tone: StatusTone = invoice.status === 'Paid' ? 'good' : invoice.status === 'Open' ? 'warning' : 'neutral';
  return (
    <Panel
      title={`${invoice.carrier} · ${invoice.period_label}`}
      subtitle={[
        invoice.number, invoice.plan,
        invoice.issued_on ? `issued ${dayOf(invoice.issued_on)}` : 'not issued yet',
        invoice.due_on ? `due ${dayOf(invoice.due_on)}` : null,
        `${invoice.members_billed} employees billed`, `total ${dollars(invoice.total)}`,
      ].filter(Boolean).join(' · ')}
      actions={<StatusChip tone={tone} label={invoice.status} />}
      collapsible
      defaultCollapsed={collapsed}
    >
      <ListTable columns={columns} rows={invoice.lines} getRowKey={(l) => l.id} pageSize={10}
        onRowClick={(l) => { if (l.request_id) onOpen(l.request_id); }}
        emptyMessage="Nothing on this invoice yet." />
    </Panel>
  );
}

// --- Service requests -------------------------------------------------------------

function Requests({
  rows, loading, onOpen, title = 'Service requests', footer,
}: {
  rows: PsRequestCard[]; loading: boolean; onOpen: (id: string) => void; title?: string; footer?: React.ReactNode;
}) {
  const columns: ListColumn<PsRequestCard>[] = [
    {
      key: 'received', header: 'Received', sortValue: (r) => r.received_at, sortStartsDescending: true, width: 130,
      render: (r) => (
        <Stack>
          <Typography variant="body2" sx={figures}>{when(r.received_at)}</Typography>
          <Typography variant="caption" sx={quiet}>{fromNow(r.received_at)}</Typography>
        </Stack>
      ),
    },
    {
      key: 'about', header: 'Employee', sortValue: (r) => r.member_name, grow: 0.9, minWidth: 150,
      render: (r) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={text}>{r.member_name || 'Not worked out'}</Typography>
          <Typography variant="caption" sx={quiet}>{r.request_label || 'Not worked out yet'}</Typography>
        </Stack>
      ),
    },
    {
      key: 'how', header: 'Answered from', sortValue: (r) => r.answered_by, width: 170,
      render: (r) => (r.answered_by === 'records' ? <StatusChip tone="good" label="Our records" />
        : r.answered_by === 'carrier' ? <StatusChip tone="neutral" label={`Call to ${r.carrier ?? 'the carrier'}`} />
          : r.carrier ? <Typography variant="body2" sx={quiet}>Needs {r.carrier}</Typography>
            : <Typography variant="body2" sx={quiet}>—</Typography>),
    },
    {
      key: 'outcome', header: 'What came of it', grow: 1.8, minWidth: 240,
      render: (r) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          {r.status === 'needs_person' ? r.needs_person_reason : r.answer_summary || r.stage}
        </Typography>
      ),
    },
    {
      key: 'status', header: 'Status', sortValue: (r) => statusChip(r).label, width: 180,
      render: (r) => <StatusChip {...statusChip(r)} />,
    },
  ];
  return (
    <Panel title={title} subtitle="Emails from this employer's HR contacts, newest first. Open one for its whole story"
      footer={footer}>
      <ListTable columns={columns} rows={rows} getRowKey={(r) => r.id} pageSize={15} loading={loading}
        onRowClick={(r) => onOpen(r.id)}
        emptyMessage="No requests yet. An email from one of this employer's HR contacts appears here within seconds." />
    </Panel>
  );
}
